import i18n from 'i18next'
import * as Yup from 'yup'
import { OfferTemplateId } from '../../../api/offers/types'
import { convertNumeralToFloatString, resolveValueByType } from '../../../utils/general'
import { REAL_ESTATE_SERVICE } from '../../constants'
import requestFields from '../../constants/requestFields'
import { propertyResponseFields } from '../../response/property'
import {
    offerModalFields,
    propertyDetailSearchFields,
    propertyExportFields,
    propertyTransactionsFields,
    realEstateDescriptionFields,
    realEstateGeneralFields,
    realEstateMaterials3DVideoFields,
} from './fields'

const propertyGeneralValueDefinition = (data) => {
    return {
        [realEstateGeneralFields.PROPERTY_LAND_USAGE_ID]: {
            [realEstateGeneralFields.PROPERTY_LAND_USAGE_ID]: data?.propertyLandusage?.id ?? '',
        },
        [realEstateGeneralFields.PROPERTY_LABEL]: {
            [realEstateGeneralFields.PROPERTY_LABEL]: data?.propertyLabel ?? '',
        },
        [realEstateGeneralFields.PROPERTY_SPACE_ID]: {
            propertyPropertySpace_group:
                data?.propertyPropertySpace?.map((x) => ({
                    property_space_id: x.propertySpace?.id?.toString(),
                    space_count: x.spaceCount,
                    space_area: x.spaceArea,
                })) ?? [],
        },
        [realEstateGeneralFields.PROPERTY_UTILITY_ID]: {
            [realEstateGeneralFields.PROPERTY_UTILITY_ID]: data?.propertyUtility?.map((item) => item.id.toString()) ?? [],
        },
        [realEstateGeneralFields.PROPERTY_SPECIAL_FEATURE_ID]: {
            [realEstateGeneralFields.PROPERTY_SPECIAL_FEATURE_ID]: data?.propertySpecialFeature?.map((item) => item.id.toString()) ?? [],
        },
        [realEstateGeneralFields.PROPERTY_EQUIPMENT_ID]: {
            [realEstateGeneralFields.PROPERTY_EQUIPMENT_ID]: data?.propertyEquipment?.map((item) => item.id?.toString()) ?? [],
        },
        [realEstateGeneralFields.PROPERTY_PERMIT_ID]: {
            [realEstateGeneralFields.PROPERTY_PERMIT_ID]: data?.propertyPermit?.map((item) => item.id?.toString()) ?? [],
        },
        [realEstateGeneralFields.PROPERTY_PUBLIC_TRANSPORTATION_ID]: {
            [realEstateGeneralFields.PROPERTY_PUBLIC_TRANSPORTATION_ID]: data?.propertyPublicTransportation?.map((item) => item.id?.toString()) ?? [],
        },
        [realEstateGeneralFields.PROPERTY_WINDOWS_DOORS_ID]: {
            [realEstateGeneralFields.PROPERTY_WINDOWS_DOORS_ID]: data?.propertyWindowsDoors?.map((item) => item.id?.toString()) ?? [],
        },
        [realEstateGeneralFields.PROPERTY_CONDITION_ID]: {
            [realEstateGeneralFields.PROPERTY_CONDITION_ID]: data?.propertyCondition?.map((item) => item.id?.toString()) ?? [],
        },
        [realEstateGeneralFields.PROPERTY_HEATING_ID]: {
            [realEstateGeneralFields.PROPERTY_HEATING_ID]: data?.propertyHeating?.map((item) => item.id?.toString()) ?? [],
        },
        [realEstateGeneralFields.PROPERTY_ORIENTATION_ID]: {
            [realEstateGeneralFields.PROPERTY_ORIENTATION_ID]: data?.propertyOrientation?.map((item) => item.id?.toString()) ?? [],
        },
        [realEstateGeneralFields.PROPERTY_ACCESS_ID]: {
            [realEstateGeneralFields.PROPERTY_ACCESS_ID]: data?.propertyAccess?.map((item) => item.id?.toString()) ?? [],
        },
        [realEstateGeneralFields.PROPERTY_DESCRIPTION]: {
            [realEstateGeneralFields.PROPERTY_DESCRIPTION]: data?.propertyDescriptions?.map((item) => item.id?.toString()) ?? [],
        },
        [realEstateGeneralFields.PROPERTY_FLOOR_POSITION_ID]: {
            [realEstateGeneralFields.PROPERTY_FLOOR_POSITION_ID]: data?.propertyFloorPosition?.map((item) => item.id?.toString()) ?? [],
        },
    }
}

const propertySharedInitialValues = (data, isSale) => {
    const valueDefinition = propertyGeneralValueDefinition(data)

    const requestVatField = isSale ? 'VAT_INCLUDED' : 'VAT_NOT_INCLUDED'
    const responseVatField = isSale ? 'vatIncluded' : 'vatNotIncluded'

    return {
        // internalAppId is used for display, id is sent in every prepareModelForSubmit
        ...(data?.internalAppId && { [realEstateGeneralFields.ID]: data.internalAppId }),
        [realEstateGeneralFields.PROPERTY_SERVICE_ID]: data?.propertyService?.id ?? '',
        [realEstateGeneralFields.PROPERTY_TYPE_ID]: data?.propertyType?.id ?? '',
        [realEstateGeneralFields.PROPERTY_SUB_TYPE_ID]: data?.propertySubType?.id ?? '',
        [realEstateGeneralFields.INTERNAL_ID]: data?.internalId ?? '',
        [realEstateGeneralFields.PROPERTY_RECEIPT_DATE]: data?.propertyReceiptDate ?? '',
        [realEstateGeneralFields.CONTRACT_NO]: data?.contractNo ?? '',
        [realEstateGeneralFields.CONTRACT_DATE]: data?.contractDate ?? '',
        [realEstateGeneralFields.OWNER_ID]: resolveValueByType(data?.owner, realEstateGeneralFields.OWNER_ID),
        [realEstateGeneralFields.PROJECT_ID]: data?.project?.id ?? '',
        [requestFields.PROPERTY_SUPPLY_SOURCE_ID]: data?.propertySupplySource?.id?.toString() ?? '',
        [realEstateGeneralFields.COUNTRY_ID]: data?.country?.id?.toString() ?? '',
        [realEstateGeneralFields.COUNTY_ID]: data?.county?.id ?? '',
        [realEstateGeneralFields.CITY_ID]: data?.city?.id ?? '',
        [realEstateGeneralFields.WIDER_AREA_ID]: data?.widerArea?.id ?? '',
        [realEstateGeneralFields.QUARTER_ID]: data?.quarter?.id ?? '',
        [realEstateGeneralFields.ISLAND_ID]: data?.island?.id ?? '',
        [realEstateGeneralFields.STREET]: data?.street ?? '',
        [realEstateGeneralFields.STREET_NUMBER]: data?.streetNumber ?? '',
        [realEstateGeneralFields.STREET_SYNC]: data?.streetSync ?? 0,
        [realEstateGeneralFields.PRICE]: data?.price?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.PRICE_CLIENT]: data?.priceClient?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.PRICE_M2]: data?.priceM2?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.PRICE_OLD]: data?.priceOld?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.PRICE_ON_REQUEST]: data?.priceOnRequest ?? false,
        [realEstateGeneralFields.DISTANCE_SEA]: data?.distanceSea ?? '',
        [realEstateGeneralFields.DISTANCE_RIVER]: data?.distanceRiver ?? '',
        [realEstateGeneralFields.DISTANCE_BEACH]: data?.distanceBeach ?? '',
        [realEstateGeneralFields.DISTANCE_CITY_CENTRE]: data?.distanceCityCentre ?? '',
        [realEstateGeneralFields.DISTANCE_SCHOOL]: data?.distanceSchool ?? '',
        [realEstateGeneralFields.DISTANCE_PUBLIC_TRANSPORTATION]: data?.distancePublicTransportation ?? '',
        [realEstateGeneralFields.AGENCY_COMMISSION_ID]: data?.agencyCommission?.id?.toString() ?? '',
        [realEstateGeneralFields.PRIMARY_AGENT_ID]: data?.primaryAgent?.id ?? '',
        [realEstateGeneralFields.AGENT_ID]: data?.agent?.map((a) => a.id.toString()) ?? [],
        [realEstateGeneralFields[requestVatField]]: data?.[responseVatField] ?? false,
        ...valueDefinition[realEstateGeneralFields.PROPERTY_SPECIAL_FEATURE_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_UTILITY_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_EQUIPMENT_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_DESCRIPTION],
    }
}

const propertySharedInitialValidation = (isSale) => {
    const vatField = isSale ? 'VAT_INCLUDED' : 'VAT_NOT_INCLUDED'

    return {
        [realEstateGeneralFields.ID]: Yup.string(),
        [realEstateGeneralFields.PROPERTY_SERVICE_ID]: Yup.string(),
        [realEstateGeneralFields.PROPERTY_TYPE_ID]: Yup.string(),
        [realEstateGeneralFields.PROPERTY_SUB_TYPE_ID]: Yup.string(),
        [realEstateGeneralFields.INTERNAL_ID]: Yup.string(),
        [realEstateGeneralFields.CONTRACT_NO]: Yup.string(),
        [realEstateGeneralFields.CONTRACT_DATE]: Yup.string(),
        [realEstateGeneralFields.OWNER_ID]: Yup.array().of(Yup.object({ [realEstateGeneralFields.OWNER_ID]: Yup.string() })),
        [realEstateGeneralFields.DATA_SOURCE_ID]: Yup.string(),
        [realEstateGeneralFields.COUNTRY_ID]: Yup.string().required(i18n.t('validation.message')),
        [realEstateGeneralFields.COUNTY_ID]: Yup.string().required(i18n.t('validation.message')),
        [realEstateGeneralFields.CITY_ID]: Yup.string().required(i18n.t('validation.message')),
        [realEstateGeneralFields.WIDER_AREA_ID]: Yup.string().required(i18n.t('validation.message')),
        [realEstateGeneralFields.STREET]: Yup.string(),
        [realEstateGeneralFields.STREET_NUMBER]: Yup.string(),
        [realEstateGeneralFields.STREET_SYNC]: Yup.number(),
        [realEstateGeneralFields.PRICE]: Yup.string(),
        [realEstateGeneralFields.PRICE_M2]: Yup.string(),
        [realEstateGeneralFields.PRICE_ON_REQUEST]: Yup.bool(),
        [realEstateGeneralFields.PROPERTY_SPECIAL_FEATURE_ID]: Yup.array().of(Yup.string()),
        [realEstateGeneralFields.PROPERTY_UTILITY_ID]: Yup.array().of(Yup.string()),
        [realEstateGeneralFields.PROPERTY_EQUIPMENT_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_DESCRIPTION]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.AGENCY_COMMISSION_ID]: Yup.string(),
        [realEstateGeneralFields.PRIMARY_AGENT_ID]: Yup.string(),
        [realEstateGeneralFields.AGENT_ID]: Yup.array().of(Yup.string()).max(3, i18n.t('validation.maxAgents')),
        [realEstateGeneralFields[vatField]]: Yup.bool(),
    }
}

const generalRealEstateValidaton = (appSettings, data) => {
    const initialValues = {
        ...propertySharedInitialValues(data),
    }
    const validationSchema = {
        ...propertySharedInitialValidation(),
    }

    const validationObject = {
        initialValues: initialValues,
        validationSchema: Yup.object(validationSchema),
        prepareModelForSubmit: (values) => ({
            ...(values[realEstateGeneralFields.ID] && { id: values[realEstateGeneralFields.ID] }),
            realEstateTypeId: values[realEstateGeneralFields.PROPERTY_TYPE_ID],
            agencyInternalId: values[realEstateGeneralFields.INTERNAL_ID],
        }),
    }

    return validationObject
}

const generalRealEstateFlatSaleValidaton = (appSettings, data) => {
    const valueDefinition = propertyGeneralValueDefinition(data)

    const validationSchema = {
        ...propertySharedInitialValidation(true),
        ...propertyFloorGroupValidation(),
        [realEstateGeneralFields.PROJECT_ID]: Yup.string(),
        [realEstateGeneralFields.PROPERTY_LABEL]: Yup.string(),
        [realEstateGeneralFields.MORTGAGE_INFO]: Yup.string(),
        [realEstateGeneralFields.EXCHANGE_POSSIBLE]: Yup.bool(),
        [realEstateGeneralFields.AREA]: Yup.string().required(i18n.t('validation.message')),
        [realEstateGeneralFields.PROPERTY_ROOMS_NO_ID]: Yup.string(),
        [realEstateGeneralFields.BEDROOMS_NO]: Yup.number(),
        [realEstateGeneralFields.BATHROOM_TOILET_NO]: Yup.number(),
        [realEstateGeneralFields.BATHROOM_NO]: Yup.number(),
        [realEstateGeneralFields.TOILET_NO]: Yup.number(),
        [realEstateGeneralFields.CONSTRUCTION_YEAR]: Yup.number(),
        [realEstateGeneralFields.ADAPTATION_YEAR]: Yup.number(),
        [realEstateGeneralFields.PROPERTY_FLOOR_ID]: Yup.string().required(i18n.t('validation.message')),
        [realEstateGeneralFields.FLOORS_TOTAL_NO]: Yup.string(),
        [realEstateGeneralFields.PROPERTY_ORIENTATION_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_HEATING_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_CONDITION_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_WINDOWS_DOORS_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_PUBLIC_TRANSPORTATION_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_PERMIT_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_ENERGY_EFFICIENCY_ID]: Yup.string(),
    }
    const initialValues = {
        ...propertySharedInitialValues(data, true),
        ...valueDefinition[realEstateGeneralFields.PROPERTY_LABEL],
        [realEstateGeneralFields.MAINTENANCE_COSTS]: data?.maintenanceCosts?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.MAINTENANCE_COSTS_TYPE]: data?.maintenanceCostsType ?? '€/m2',
        [realEstateGeneralFields.MORTGAGE_INFO]: data?.mortgageInfo ?? '',
        [realEstateGeneralFields.EXCHANGE_POSSIBLE]: data?.exchangePossible ?? '',
        [realEstateGeneralFields.AREA]: data?.area?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.AREA_NETT]: data?.areaNett?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.AREA_GARDEN]: data?.areaGarden?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.PROPERTY_ROOMS_NO_ID]: data?.propertyRoomsNo?.id ?? '',
        [realEstateGeneralFields.BEDROOMS_NO]: data?.bedroomsNo ?? '',
        [realEstateGeneralFields.BATHROOM_TOILET_NO]: data?.bathroomToiletNo ?? '',
        [realEstateGeneralFields.BATHROOM_NO]: data?.bathroomNo ?? '',
        [realEstateGeneralFields.TOILET_NO]: data?.toiletNo ?? '',
        [realEstateGeneralFields.CEILING_HEIGHT]: data?.ceilingHeight ?? '',
        [realEstateGeneralFields.CONSTRUCTION_YEAR]: data?.constructionYear ?? '',
        [realEstateGeneralFields.ADAPTATION_YEAR]: data?.adaptationYear ?? '',
        ...valueDefinition[realEstateGeneralFields.PROPERTY_FLOOR_POSITION_ID],
        [realEstateGeneralFields.PROPERTY_FLOOR_ID]: data?.propertyFloor?.id ?? '',
        [realEstateGeneralFields.FLOOR_NO]: data?.floorNo ?? '',
        [realEstateGeneralFields.FLOORS_TOTAL_NO]: data?.floorsTotalNo ?? '',
        [realEstateGeneralFields.PRICE_INITIAL]: data?.priceInitial?.toString().replace(/\./g, ',') ?? '',
        ...valueDefinition[realEstateGeneralFields.PROPERTY_ORIENTATION_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_HEATING_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_CONDITION_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_WINDOWS_DOORS_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_PUBLIC_TRANSPORTATION_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_PERMIT_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_SPACE_ID],
        [realEstateGeneralFields.PROPERTY_ENERGY_EFFICIENCY_ID]: data?.propertyEnergyEfficiency?.id ?? '',
    }

    const validationObject = {
        initialValues: initialValues,
        validationSchema: Yup.object().shape(validationSchema, [['floor_no', 'property_floor_position_id']]),
        prepareModelForSubmit: (values) => ({
            ...values,
            ...(data?.id && { [realEstateGeneralFields.ID]: data.id }),
            [realEstateGeneralFields.MORTGAGE_INFO]: convertNumeralToFloatString(values?.[realEstateGeneralFields.MORTGAGE_INFO]),
            [realEstateGeneralFields.MAINTENANCE_COSTS]: convertNumeralToFloatString(values?.[realEstateGeneralFields.MAINTENANCE_COSTS]),
            [realEstateGeneralFields.AREA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.AREA]),
            [realEstateGeneralFields.AREA_NETT]: convertNumeralToFloatString(values?.[realEstateGeneralFields.AREA_NETT]),
            [realEstateGeneralFields.AREA_GARDEN]: convertNumeralToFloatString(values?.[realEstateGeneralFields.AREA_GARDEN]),
            [realEstateGeneralFields.CEILING_HEIGHT]: convertNumeralToFloatString(values?.[realEstateGeneralFields.CEILING_HEIGHT]),
            [realEstateGeneralFields.DISTANCE_SEA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_SEA]),
            [realEstateGeneralFields.DISTANCE_RIVER]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_RIVER]),
            [realEstateGeneralFields.DISTANCE_BEACH]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_BEACH]),
            [realEstateGeneralFields.DISTANCE_CITY_CENTRE]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_CITY_CENTRE]),
            [realEstateGeneralFields.DISTANCE_SCHOOL]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_SCHOOL]),
            [realEstateGeneralFields.DISTANCE_PUBLIC_TRANSPORTATION]: convertNumeralToFloatString(
                values?.[realEstateGeneralFields.DISTANCE_PUBLIC_TRANSPORTATION]
            ),
        }),
    }

    return validationObject
}

const generalRealEstateFlatLeaseValidaton = (appSettings, data) => {
    const valueDefinition = propertyGeneralValueDefinition(data)
    const validationSchema = {
        ...propertySharedInitialValidation(false),
        ...propertyFloorGroupValidation(),
        [realEstateGeneralFields.PROPERTY_LABEL]: Yup.string(),
        [realEstateGeneralFields.PARKING_SPACE_PRICE]: Yup.string(),
        [realEstateGeneralFields.UTILITIES_INCLUDED]: Yup.bool(),
        [realEstateGeneralFields.AREA]: Yup.string().required(i18n.t('validation.message')),
        [realEstateGeneralFields.PROPERTY_ROOMS_NO_ID]: Yup.string(),
        [realEstateGeneralFields.BEDROOMS_NO]: Yup.number(),
        [realEstateGeneralFields.BATHROOM_TOILET_NO]: Yup.number(),
        [realEstateGeneralFields.BATHROOM_NO]: Yup.number(),
        [realEstateGeneralFields.TOILET_NO]: Yup.number(),
        [realEstateGeneralFields.CONSTRUCTION_YEAR]: Yup.number(),
        [realEstateGeneralFields.ADAPTATION_YEAR]: Yup.number(),
        [realEstateGeneralFields.PROPERTY_FLOOR_ID]: Yup.string(),
        [realEstateGeneralFields.FLOORS_TOTAL_NO]: Yup.string(),
        [realEstateGeneralFields.PROPERTY_ORIENTATION_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_HEATING_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_CONDITION_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_WINDOWS_DOORS_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_PUBLIC_TRANSPORTATION_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_ENERGY_EFFICIENCY_ID]: Yup.string(),
    }

    const initialValues = {
        ...propertySharedInitialValues(data, false),
        [realEstateGeneralFields.PROPERTY_LABEL]: data?.propertyLabel ?? '',
        [realEstateGeneralFields.AVAILABLE_FROM_DATE]: data?.rentedToDate ?? '',
        [realEstateGeneralFields.MAINTENANCE_COSTS]: data?.maintenanceCosts?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.MAINTENANCE_COSTS_TYPE]: data?.maintenanceCostsType ?? '€/m2',
        [realEstateGeneralFields.COMMON_UTILITY_COSTS]: data?.commonUtilityCosts?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.COMMON_UTILITY_COSTS_TYPE]: data?.commonUtilityCostsType ?? '€/m2',
        [realEstateGeneralFields.PARKING_SPACE_PRICE]: data?.parkingSpacePrice?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.UTILITIES_INCLUDED]: data?.utilitiesIncluded ?? '',
        [realEstateGeneralFields.AREA]: data?.area?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.AREA_NETT]: data?.areaNett?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.AREA_GARDEN]: data?.areaGarden?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.PROPERTY_ROOMS_NO_ID]: data?.propertyRoomsNo?.id ?? '',
        [realEstateGeneralFields.BEDROOMS_NO]: data?.bedroomsNo ?? '',
        [realEstateGeneralFields.BATHROOM_TOILET_NO]: data?.bathroomToiletNo ?? '',
        [realEstateGeneralFields.BATHROOM_NO]: data?.bathroomNo ?? '',
        [realEstateGeneralFields.TOILET_NO]: data?.toiletNo ?? '',
        [realEstateGeneralFields.CEILING_HEIGHT]: data?.ceilingHeight ?? '',
        [realEstateGeneralFields.CONSTRUCTION_YEAR]: data?.constructionYear ?? '',
        [realEstateGeneralFields.ADAPTATION_YEAR]: data?.adaptationYear ?? '',
        ...valueDefinition[realEstateGeneralFields.PROPERTY_FLOOR_POSITION_ID],
        [realEstateGeneralFields.PROPERTY_FLOOR_ID]: data?.propertyFloor?.id ?? '',
        [realEstateGeneralFields.FLOOR_NO]: data?.floorNo ?? '',
        [realEstateGeneralFields.FLOORS_TOTAL_NO]: data?.floorsTotalNo ?? '',
        ...valueDefinition[realEstateGeneralFields.PROPERTY_ORIENTATION_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_HEATING_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_CONDITION_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_WINDOWS_DOORS_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_PUBLIC_TRANSPORTATION_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_SPACE_ID],
        [realEstateGeneralFields.PROPERTY_ENERGY_EFFICIENCY_ID]: data?.propertyEnergyEfficiency?.id ?? '',
        ...valueDefinition[realEstateGeneralFields.PROPERTY_PERMIT_ID],
    }

    const validationObject = {
        initialValues: initialValues,
        validationSchema: Yup.object().shape(validationSchema, [['floor_no', 'property_floor_position_id']]),
        prepareModelForSubmit: (values) => ({
            ...values,
            ...(data?.id && { [realEstateGeneralFields.ID]: data.id }),
            [realEstateGeneralFields.MAINTENANCE_COSTS]: convertNumeralToFloatString(values?.[realEstateGeneralFields.MAINTENANCE_COSTS]),
            [realEstateGeneralFields.COMMON_UTILITY_COSTS]: convertNumeralToFloatString(values?.[realEstateGeneralFields.COMMON_UTILITY_COSTS]),
            [realEstateGeneralFields.DISTANCE_SEA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_SEA]),
            [realEstateGeneralFields.DISTANCE_RIVER]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_RIVER]),
            [realEstateGeneralFields.DISTANCE_BEACH]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_BEACH]),
            [realEstateGeneralFields.DISTANCE_CITY_CENTRE]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_CITY_CENTRE]),
            [realEstateGeneralFields.DISTANCE_SCHOOL]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_SCHOOL]),
            [realEstateGeneralFields.DISTANCE_PUBLIC_TRANSPORTATION]: convertNumeralToFloatString(
                values?.[realEstateGeneralFields.DISTANCE_PUBLIC_TRANSPORTATION]
            ),
            [realEstateGeneralFields.AREA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.AREA]),
            [realEstateGeneralFields.AREA_NETT]: convertNumeralToFloatString(values?.[realEstateGeneralFields.AREA_NETT]),
            [realEstateGeneralFields.AREA_GARDEN]: convertNumeralToFloatString(values?.[realEstateGeneralFields.AREA_GARDEN]),
            [realEstateGeneralFields.CEILING_HEIGHT]: convertNumeralToFloatString(values?.[realEstateGeneralFields.CEILING_HEIGHT]),
            [realEstateGeneralFields.PARKING_SPACE_PRICE]: convertNumeralToFloatString(values?.[realEstateGeneralFields.PARKING_SPACE_PRICE]),
        }),
    }

    return validationObject
}

const propertyGeneralHouseSaleValidaton = (appSettings, data) => {
    const valueDefinition = propertyGeneralValueDefinition(data)
    const validationSchema = {
        ...propertySharedInitialValidation(true),
        [realEstateGeneralFields.PROJECT_ID]: Yup.string(),
        [realEstateGeneralFields.PROPERTY_HOUSE_TYPE_ID]: Yup.string(),
        [realEstateGeneralFields.MORTGAGE_INFO]: Yup.string(),
        [realEstateGeneralFields.EXCHANGE_POSSIBLE]: Yup.bool(),
        [realEstateGeneralFields.AREA]: Yup.string().required(i18n.t('validation.message')),
        [realEstateGeneralFields.ROOMS_NO]: Yup.string(),
        [realEstateGeneralFields.BEDROOMS_NO]: Yup.number(),
        [realEstateGeneralFields.BED_NO]: Yup.number(),
        [realEstateGeneralFields.BATHROOM_TOILET_NO]: Yup.number(),
        [realEstateGeneralFields.BATHROOM_NO]: Yup.number(),
        [realEstateGeneralFields.TOILET_NO]: Yup.number(),
        [realEstateGeneralFields.CONSTRUCTION_YEAR]: Yup.number(),
        [realEstateGeneralFields.ADAPTATION_YEAR]: Yup.number(),
        [realEstateGeneralFields.PROPERTY_FLOOR_ID]: Yup.string().required(i18n.t('validation.message')),
        [realEstateGeneralFields.PROPERTY_ORIENTATION_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_HEATING_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_CONDITION_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_WINDOWS_DOORS_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_PUBLIC_TRANSPORTATION_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_PERMIT_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_ENERGY_EFFICIENCY_ID]: Yup.string(),
    }

    const initialValues = {
        ...propertySharedInitialValues(data, true),
        [realEstateGeneralFields.PROPERTY_HOUSE_TYPE_ID]: data?.propertyHouseType?.id ?? '',
        [realEstateGeneralFields.MORTGAGE_INFO]: data?.mortgageInfo ?? '',
        [realEstateGeneralFields.EXCHANGE_POSSIBLE]: data?.exchangePossible ?? '',
        [realEstateGeneralFields.AREA]: data?.area?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.AREA_NETT]: data?.areaNett?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.AREA_GARDEN]: data?.areaGarden?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.PLOT_AREA]: data?.plotArea?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.ROOMS_NO]: data?.roomsNo ?? '',
        [realEstateGeneralFields.BEDROOMS_NO]: data?.bedroomsNo ?? '',
        [realEstateGeneralFields.HOUSING_UNITS_NUMBER]: data?.housingUnitsNumber ?? '',
        [realEstateGeneralFields.BED_NO]: data?.bedNo ?? '',
        [realEstateGeneralFields.BATHROOM_TOILET_NO]: data?.bathroomToiletNo ?? '',
        [realEstateGeneralFields.BATHROOM_NO]: data?.bathroomNo ?? '',
        [realEstateGeneralFields.TOILET_NO]: data?.toiletNo ?? '',
        [realEstateGeneralFields.CEILING_HEIGHT]: data?.ceilingHeight ?? '',
        [realEstateGeneralFields.CONSTRUCTION_YEAR]: data?.constructionYear ?? '',
        [realEstateGeneralFields.ADAPTATION_YEAR]: data?.adaptationYear ?? '',
        [realEstateGeneralFields.PROPERTY_FLOOR_ID]: data?.propertyFloor?.id ?? '',
        [realEstateGeneralFields.PRICE_INITIAL]: data?.priceInitial?.toString().replace(/\./g, ',') ?? '',
        ...valueDefinition[realEstateGeneralFields.PROPERTY_ORIENTATION_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_HEATING_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_CONDITION_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_WINDOWS_DOORS_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_PUBLIC_TRANSPORTATION_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_PERMIT_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_SPACE_ID],
        [realEstateGeneralFields.PROPERTY_ENERGY_EFFICIENCY_ID]: data?.propertyEnergyEfficiency?.id ?? '',
    }

    const validationObject = {
        initialValues: initialValues,
        validationSchema: Yup.object(validationSchema),
        prepareModelForSubmit: (values) => ({
            ...values,
            ...(data?.id && { [realEstateGeneralFields.ID]: data.id }),
            [realEstateGeneralFields.MORTGAGE_INFO]: convertNumeralToFloatString(values?.[realEstateGeneralFields.MORTGAGE_INFO]),
            [realEstateGeneralFields.CEILING_HEIGHT]: convertNumeralToFloatString(values?.[realEstateGeneralFields.CEILING_HEIGHT]),
            [realEstateGeneralFields.DISTANCE_SEA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_SEA]),
            [realEstateGeneralFields.DISTANCE_RIVER]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_RIVER]),
            [realEstateGeneralFields.DISTANCE_BEACH]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_BEACH]),
            [realEstateGeneralFields.DISTANCE_CITY_CENTRE]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_CITY_CENTRE]),
            [realEstateGeneralFields.DISTANCE_SCHOOL]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_SCHOOL]),
            [realEstateGeneralFields.DISTANCE_PUBLIC_TRANSPORTATION]: convertNumeralToFloatString(
                values?.[realEstateGeneralFields.DISTANCE_PUBLIC_TRANSPORTATION]
            ),
            [realEstateGeneralFields.AREA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.AREA]),
            [realEstateGeneralFields.AREA_NETT]: convertNumeralToFloatString(values?.[realEstateGeneralFields.AREA_NETT]),
            [realEstateGeneralFields.AREA_GARDEN]: convertNumeralToFloatString(values?.[realEstateGeneralFields.AREA_GARDEN]),
            [realEstateGeneralFields.PLOT_AREA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.PLOT_AREA]),
        }),
    }

    return validationObject
}

const propertyGeneralHouseLeaseValidaton = (appSettings, data) => {
    const valueDefinition = propertyGeneralValueDefinition(data)
    const validationSchema = {
        ...propertySharedInitialValidation(false),
        [realEstateGeneralFields.UTILITIES_INCLUDED]: Yup.bool(),
        [realEstateGeneralFields.AREA]: Yup.string().required(i18n.t('validation.message')),
        [realEstateGeneralFields.PROPERTY_HOUSE_TYPE_ID]: Yup.string(),
        [realEstateGeneralFields.ROOMS_NO]: Yup.string(),
        [realEstateGeneralFields.BEDROOMS_NO]: Yup.number(),
        [realEstateGeneralFields.BED_NO]: Yup.number(),
        [realEstateGeneralFields.BATHROOM_TOILET_NO]: Yup.number(),
        [realEstateGeneralFields.BATHROOM_NO]: Yup.number(),
        [realEstateGeneralFields.TOILET_NO]: Yup.number(),
        [realEstateGeneralFields.CONSTRUCTION_YEAR]: Yup.number(),
        [realEstateGeneralFields.ADAPTATION_YEAR]: Yup.number(),
        [realEstateGeneralFields.PROPERTY_FLOOR_ID]: Yup.string(),
        [realEstateGeneralFields.PROPERTY_ORIENTATION_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_HEATING_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_CONDITION_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_WINDOWS_DOORS_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_PUBLIC_TRANSPORTATION_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_ENERGY_EFFICIENCY_ID]: Yup.string(),
    }

    const initialValues = {
        ...propertySharedInitialValues(data, false),
        [realEstateGeneralFields.PROPERTY_HOUSE_TYPE_ID]: data?.propertyHouseType?.id ?? '',
        [realEstateGeneralFields.AVAILABLE_FROM_DATE]: data?.rentedToDate ?? '',
        [realEstateGeneralFields.UTILITIES_INCLUDED]: data?.utilitiesIncluded ?? false,
        [realEstateGeneralFields.AREA]: data?.area?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.AREA_NETT]: data?.areaNett?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.AREA_GARDEN]: data?.areaGarden?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.PLOT_AREA]: data?.plotArea?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.ROOMS_NO]: data?.roomsNo ?? '',
        [realEstateGeneralFields.BEDROOMS_NO]: data?.bedroomsNo ?? '',
        [realEstateGeneralFields.HOUSING_UNITS_NUMBER]: data?.housingUnitsNumber ?? '',
        [realEstateGeneralFields.BED_NO]: data?.bedNo ?? '',
        [realEstateGeneralFields.BATHROOM_TOILET_NO]: data?.bathroomToiletNo ?? '',
        [realEstateGeneralFields.BATHROOM_NO]: data?.bathroomNo ?? '',
        [realEstateGeneralFields.TOILET_NO]: data?.toiletNo ?? '',
        [realEstateGeneralFields.CEILING_HEIGHT]: data?.ceilingHeight ?? '',
        [realEstateGeneralFields.CONSTRUCTION_YEAR]: data?.constructionYear ?? '',
        [realEstateGeneralFields.ADAPTATION_YEAR]: data?.adaptationYear ?? '',
        [realEstateGeneralFields.PROPERTY_FLOOR_ID]: data?.propertyFloor?.id ?? '',
        ...valueDefinition[realEstateGeneralFields.PROPERTY_ORIENTATION_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_HEATING_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_CONDITION_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_WINDOWS_DOORS_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_PUBLIC_TRANSPORTATION_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_SPACE_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_PERMIT_ID],
        [realEstateGeneralFields.PROPERTY_ENERGY_EFFICIENCY_ID]: data?.propertyEnergyEfficiency?.id ?? '',
    }

    const validationObject = {
        initialValues: initialValues,
        validationSchema: Yup.object(validationSchema),
        prepareModelForSubmit: (values) => ({
            ...values,
            ...(data?.id && { [realEstateGeneralFields.ID]: data.id }),
            [realEstateGeneralFields.CEILING_HEIGHT]: convertNumeralToFloatString(values?.[realEstateGeneralFields.CEILING_HEIGHT]),
            [realEstateGeneralFields.DISTANCE_SEA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_SEA]),
            [realEstateGeneralFields.DISTANCE_RIVER]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_RIVER]),
            [realEstateGeneralFields.DISTANCE_BEACH]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_BEACH]),
            [realEstateGeneralFields.DISTANCE_CITY_CENTRE]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_CITY_CENTRE]),
            [realEstateGeneralFields.DISTANCE_SCHOOL]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_SCHOOL]),
            [realEstateGeneralFields.DISTANCE_PUBLIC_TRANSPORTATION]: convertNumeralToFloatString(
                values?.[realEstateGeneralFields.DISTANCE_PUBLIC_TRANSPORTATION]
            ),
            [realEstateGeneralFields.AREA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.AREA]),
            [realEstateGeneralFields.AREA_NETT]: convertNumeralToFloatString(values?.[realEstateGeneralFields.AREA_NETT]),
            [realEstateGeneralFields.AREA_GARDEN]: convertNumeralToFloatString(values?.[realEstateGeneralFields.AREA_GARDEN]),
            [realEstateGeneralFields.PLOT_AREA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.PLOT_AREA]),
        }),
    }

    return validationObject
}

const propertyGeneralOfficeSpaceSaleValidaton = (appSettings, data) => {
    const valueDefinition = propertyGeneralValueDefinition(data)

    const validationSchema = {
        ...propertySharedInitialValidation(true),
        ...propertyFloorGroupValidation(),
        [realEstateGeneralFields.PROJECT_ID]: Yup.string(),
        [realEstateGeneralFields.PROPERTY_LABEL]: Yup.string(),
        [realEstateGeneralFields.PROPERTY_POSITION_ID]: Yup.number(),
        [realEstateGeneralFields.MORTGAGE_INFO]: Yup.string(),
        [realEstateGeneralFields.EXCHANGE_POSSIBLE]: Yup.bool(),
        [realEstateGeneralFields.AREA]: Yup.string().required(i18n.t('validation.message')),
        [realEstateGeneralFields.ROOMS_NO]: Yup.string().required(i18n.t('validation.message')),
        [realEstateGeneralFields.COMMON_BATHROOM_NO]: Yup.number(),
        [realEstateGeneralFields.TOILET_NO]: Yup.number(),
        [realEstateGeneralFields.CONSTRUCTION_YEAR]: Yup.number(),
        [realEstateGeneralFields.ADAPTATION_YEAR]: Yup.number(),
        [realEstateGeneralFields.FLOORS_TOTAL_NO]: Yup.number(),
        [realEstateGeneralFields.PROPERTY_ORIENTATION_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_HEATING_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_CONDITION_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_WINDOWS_DOORS_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_PUBLIC_TRANSPORTATION_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_PERMIT_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_ENERGY_EFFICIENCY_ID]: Yup.string(),
        [realEstateGeneralFields.PROPERTY_ACCESS_ID]: Yup.array().of(Yup.number()),
    }

    const initialValues = {
        ...propertySharedInitialValues(data, true),
        [realEstateGeneralFields.PROPERTY_PRIMARY_USAGE_ID]: data?.propertyPrimaryUsage?.id ?? '',
        [realEstateGeneralFields.PROPERTY_LABEL]: data?.propertyLabel ?? '',
        [realEstateGeneralFields.PROPERTY_POSITION_ID]: data?.propertyPosition?.id ?? '',
        [realEstateGeneralFields.MORTGAGE_INFO]: data?.mortgageInfo ?? '',
        [realEstateGeneralFields.EXCHANGE_POSSIBLE]: data?.exchangePossible ?? '',
        [realEstateGeneralFields.AREA]: data?.area?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.PLOT_AREA]: data?.plotArea?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.AREA_GARDEN]: data?.areaGarden?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.AREA_STORAGE]: data?.areaStorage?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.PARKING_SPACE_NUMBER]: data?.parkingSpaceNumber ?? '',
        [realEstateGeneralFields.ROOMS_NO]: data?.roomsNo ?? '',
        [realEstateGeneralFields.COMMON_BATHROOM_NO]: data?.commBathroomNo ?? '',
        [realEstateGeneralFields.TOILET_NO]: data?.toiletNo ?? '',
        [realEstateGeneralFields.CEILING_HEIGHT]: data?.ceilingHeight ?? '',
        [realEstateGeneralFields.CONSTRUCTION_YEAR]: data?.constructionYear ?? '',
        [realEstateGeneralFields.ADAPTATION_YEAR]: data?.adaptationYear ?? '',
        [realEstateGeneralFields.FLOOR_NO]: data?.floorNo ?? '',
        [realEstateGeneralFields.FLOORS_TOTAL_NO]: data?.floorsTotalNo ?? '',
        [realEstateGeneralFields.PRICE_INITIAL]: data?.priceInitial?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.PROPERTY_ENERGY_EFFICIENCY_ID]: data?.propertyEnergyEfficiency?.id ?? '',
        [realEstateGeneralFields.MAINTENANCE_COSTS]: data?.maintenanceCosts?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.MAINTENANCE_COSTS_TYPE]: data?.maintenanceCostsType ?? '€/m2',
        ...valueDefinition[realEstateGeneralFields.PROPERTY_FLOOR_POSITION_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_ORIENTATION_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_HEATING_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_CONDITION_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_WINDOWS_DOORS_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_PUBLIC_TRANSPORTATION_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_PERMIT_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_SPACE_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_ACCESS_ID],
    }

    const validationObject = {
        initialValues: initialValues,
        validationSchema: Yup.object().shape(validationSchema, [['floor_no', 'property_floor_position_id']]),
        prepareModelForSubmit: (values) => ({
            ...values,
            ...(data?.id && { [realEstateGeneralFields.ID]: data.id }),
            [realEstateGeneralFields.MORTGAGE_INFO]: convertNumeralToFloatString(values?.[realEstateGeneralFields.MORTGAGE_INFO]),
            [realEstateGeneralFields.MAINTENANCE_COSTS]: convertNumeralToFloatString(values?.[realEstateGeneralFields.MAINTENANCE_COSTS]),
            [realEstateGeneralFields.CEILING_HEIGHT]: convertNumeralToFloatString(values?.[realEstateGeneralFields.CEILING_HEIGHT]),
            [realEstateGeneralFields.DISTANCE_SEA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_SEA]),
            [realEstateGeneralFields.DISTANCE_RIVER]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_RIVER]),
            [realEstateGeneralFields.DISTANCE_BEACH]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_BEACH]),
            [realEstateGeneralFields.DISTANCE_CITY_CENTRE]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_CITY_CENTRE]),
            [realEstateGeneralFields.DISTANCE_SCHOOL]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_SCHOOL]),
            [realEstateGeneralFields.DISTANCE_PUBLIC_TRANSPORTATION]: convertNumeralToFloatString(
                values?.[realEstateGeneralFields.DISTANCE_PUBLIC_TRANSPORTATION]
            ),
            [realEstateGeneralFields.AREA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.AREA]),
            [realEstateGeneralFields.AREA_STORAGE]: convertNumeralToFloatString(values?.[realEstateGeneralFields.AREA_STORAGE]),
            [realEstateGeneralFields.AREA_GARDEN]: convertNumeralToFloatString(values?.[realEstateGeneralFields.AREA_GARDEN]),
            [realEstateGeneralFields.PLOT_AREA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.PLOT_AREA]),
        }),
    }

    return validationObject
}

const propertyGeneralOfficeSpaceLeaseValidaton = (appSettings, data) => {
    const valueDefinition = propertyGeneralValueDefinition(data)

    const validationSchema = {
        ...propertySharedInitialValidation(false),
        ...propertyFloorGroupValidation(),
        [realEstateGeneralFields.PROPERTY_LABEL]: Yup.string(),
        [realEstateGeneralFields.PROPERTY_POSITION_ID]: Yup.number(),
        [realEstateGeneralFields.PARKING_SPACE_PRICE]: Yup.number(),
        [realEstateGeneralFields.UTILITIES_INCLUDED]: Yup.bool(),
        [realEstateGeneralFields.AREA]: Yup.string().required(i18n.t('validation.message')),
        [realEstateGeneralFields.ROOMS_NO]: Yup.string().required(i18n.t('validation.message')),
        [realEstateGeneralFields.COMMON_BATHROOM_NO]: Yup.number(),
        [realEstateGeneralFields.TOILET_NO]: Yup.number(),
        [realEstateGeneralFields.CONSTRUCTION_YEAR]: Yup.number(),
        [realEstateGeneralFields.ADAPTATION_YEAR]: Yup.number(),
        [realEstateGeneralFields.FLOORS_TOTAL_NO]: Yup.number(),
        [realEstateGeneralFields.PROPERTY_ORIENTATION_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_HEATING_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_CONDITION_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_WINDOWS_DOORS_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_PUBLIC_TRANSPORTATION_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_ENERGY_EFFICIENCY_ID]: Yup.string(),
        [realEstateGeneralFields.PROPERTY_ACCESS_ID]: Yup.array().of(Yup.number()),
    }

    const initialValues = {
        ...propertySharedInitialValues(data),
        [realEstateGeneralFields.AVAILABLE_FROM_DATE]: data?.rentedToDate ?? '',
        [realEstateGeneralFields.PROPERTY_PRIMARY_USAGE_ID]: data?.propertyPrimaryUsage?.id ?? '',
        [realEstateGeneralFields.PROPERTY_LABEL]: data?.propertyLabel ?? '',
        [realEstateGeneralFields.PROPERTY_POSITION_ID]: data?.propertyPosition?.id ?? '',
        [realEstateGeneralFields.MAINTENANCE_COSTS]: data?.maintenanceCosts?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.MAINTENANCE_COSTS_TYPE]: data?.maintenanceCostsType ?? '€/m2',
        [realEstateGeneralFields.COMMON_MAINTENANCE_COSTS]: data?.commonMaintenanceCosts?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.COMMON_MAINTENANCE_COSTS_TYPE]: data?.commonMaintenanceCostsType ?? '€/m2',
        [realEstateGeneralFields.COMMON_UTILITY_COSTS]: data?.commonUtilityCosts?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.COMMON_UTILITY_COSTS_TYPE]: data?.commonUtilityCostsType ?? '€/m2',
        [realEstateGeneralFields.PARKING_SPACE_PRICE]: data?.parkingSpacePrice?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.UTILITIES_INCLUDED]: data?.utilitiesIncluded ?? false,
        [realEstateGeneralFields.AREA]: data?.area?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.AREA_GARDEN]: data?.areaGarden?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.PLOT_AREA]: data?.plotArea?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.AREA_STORAGE]: data?.areaStorage?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.PARKING_SPACE_NUMBER]: data?.parkingSpaceNumber ?? '',
        [realEstateGeneralFields.ROOMS_NO]: data?.roomsNo ?? '',
        [realEstateGeneralFields.COMMON_BATHROOM_NO]: data?.commBathroomNo ?? '',
        [realEstateGeneralFields.TOILET_NO]: data?.toiletNo ?? '',
        [realEstateGeneralFields.CEILING_HEIGHT]: data?.ceilingHeight ?? '',
        [realEstateGeneralFields.CONSTRUCTION_YEAR]: data?.constructionYear ?? '',
        [realEstateGeneralFields.ADAPTATION_YEAR]: data?.adaptationYear ?? '',
        [realEstateGeneralFields.FLOOR_NO]: data?.floorNo ?? '',
        [realEstateGeneralFields.FLOORS_TOTAL_NO]: data?.floorsTotalNo ?? '',
        [realEstateGeneralFields.PROPERTY_ENERGY_EFFICIENCY_ID]: data?.propertyEnergyEfficiency?.id ?? '',
        ...valueDefinition[realEstateGeneralFields.PROPERTY_FLOOR_POSITION_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_ORIENTATION_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_HEATING_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_CONDITION_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_WINDOWS_DOORS_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_PUBLIC_TRANSPORTATION_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_PERMIT_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_SPACE_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_ACCESS_ID],
    }

    const validationObject = {
        initialValues: initialValues,
        validationSchema: Yup.object().shape(validationSchema, [['floor_no', 'property_floor_position_id']]),
        prepareModelForSubmit: (values) => ({
            ...values,
            ...(data?.id && { [realEstateGeneralFields.ID]: data.id }),
            [realEstateGeneralFields.MAINTENANCE_COSTS]: convertNumeralToFloatString(values?.[realEstateGeneralFields.MAINTENANCE_COSTS]),
            [realEstateGeneralFields.COMMON_MAINTENANCE_COSTS]: convertNumeralToFloatString(
                values?.[realEstateGeneralFields.COMMON_MAINTENANCE_COSTS],
                true
            ),
            [realEstateGeneralFields.COMMON_UTILITY_COSTS]: convertNumeralToFloatString(values?.[realEstateGeneralFields.COMMON_UTILITY_COSTS]),
            [realEstateGeneralFields.CEILING_HEIGHT]: convertNumeralToFloatString(values?.[realEstateGeneralFields.CEILING_HEIGHT]),
            [realEstateGeneralFields.DISTANCE_SEA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_SEA]),
            [realEstateGeneralFields.DISTANCE_RIVER]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_RIVER]),
            [realEstateGeneralFields.DISTANCE_BEACH]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_BEACH]),
            [realEstateGeneralFields.DISTANCE_CITY_CENTRE]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_CITY_CENTRE]),
            [realEstateGeneralFields.DISTANCE_SCHOOL]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_SCHOOL]),
            [realEstateGeneralFields.DISTANCE_PUBLIC_TRANSPORTATION]: convertNumeralToFloatString(
                values?.[realEstateGeneralFields.DISTANCE_PUBLIC_TRANSPORTATION]
            ),
            [realEstateGeneralFields.AREA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.AREA]),
            [realEstateGeneralFields.AREA_STORAGE]: convertNumeralToFloatString(values?.[realEstateGeneralFields.AREA_STORAGE]),
            [realEstateGeneralFields.AREA_GARDEN]: convertNumeralToFloatString(values?.[realEstateGeneralFields.AREA_GARDEN]),
            [realEstateGeneralFields.PLOT_AREA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.PLOT_AREA]),
            [realEstateGeneralFields.PARKING_SPACE_PRICE]: convertNumeralToFloatString(values?.[realEstateGeneralFields.PARKING_SPACE_PRICE]),
        }),
    }

    return validationObject
}

const propertyGeneralLandSaleValidaton = (appSettings, data) => {
    const valueDefinition = propertyGeneralValueDefinition(data)
    const validationSchema = {
        ...propertySharedInitialValidation(true),
        [realEstateGeneralFields.PROJECT_ID]: Yup.string(),
        [realEstateGeneralFields.PROPERTY_LAND_USAGE_ID]: Yup.number(),
        [realEstateGeneralFields.MORTGAGE_INFO]: Yup.string(),
        [realEstateGeneralFields.AREA]: Yup.string().required(i18n.t('validation.message')),
        [realEstateGeneralFields.CONSTRUCTION_FLOORS_NO]: Yup.number(),
        [realEstateGeneralFields.PROPERTY_ORIENTATION_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_PUBLIC_TRANSPORTATION_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_PERMIT_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_ACCESS_ID]: Yup.array().of(Yup.number()),
    }

    const initialValues = {
        ...propertySharedInitialValues(data, true),
        [realEstateGeneralFields.PROPERTY_LAND_USAGE_ID]: data?.propertyLandUsage?.id ?? '',
        [realEstateGeneralFields.MORTGAGE_INFO]: data?.mortgageInfo ?? '',
        [realEstateGeneralFields.EXCHANGE_POSSIBLE]: data?.exchangePossible ?? '',
        [realEstateGeneralFields.AREA]: data?.area?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.PLOT_WIDTH]: data?.plotWidth ?? '',
        [realEstateGeneralFields.PLOT_LENGTH]: data?.plotLength ?? '',
        [realEstateGeneralFields.CONSTRUCTION_COEFFICIENT]: data?.constructionCoefficient ?? '',
        [realEstateGeneralFields.CONSTRUCTION_FLOORS_NO]: data?.constructionFloorsNo ?? '',
        [realEstateGeneralFields.MAX_CONSTRUCTION_AREA]: data?.maxConstructionArea ?? '',
        [realEstateGeneralFields.PRICE_INITIAL]: data?.priceInitial?.toString().replace(/\./g, ',') ?? '',
        ...valueDefinition[realEstateGeneralFields.PROPERTY_ORIENTATION_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_PUBLIC_TRANSPORTATION_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_PERMIT_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_ACCESS_ID],
        [realEstateGeneralFields.DISTANCE_PATHWAY]: data?.distancePathway ?? '',
    }

    const validationObject = {
        initialValues: initialValues,
        validationSchema: Yup.object(validationSchema),
        prepareModelForSubmit: (values) => ({
            ...values,
            ...(data?.id && { [realEstateGeneralFields.ID]: data.id }),
            [realEstateGeneralFields.MORTGAGE_INFO]: convertNumeralToFloatString(values?.[realEstateGeneralFields.MORTGAGE_INFO]),
            [realEstateGeneralFields.DISTANCE_SEA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_SEA]),
            [realEstateGeneralFields.DISTANCE_RIVER]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_RIVER]),
            [realEstateGeneralFields.DISTANCE_BEACH]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_BEACH]),
            [realEstateGeneralFields.DISTANCE_CITY_CENTRE]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_CITY_CENTRE]),
            [realEstateGeneralFields.DISTANCE_SCHOOL]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_SCHOOL]),
            [realEstateGeneralFields.DISTANCE_PUBLIC_TRANSPORTATION]: convertNumeralToFloatString(
                values?.[realEstateGeneralFields.DISTANCE_PUBLIC_TRANSPORTATION]
            ),
            [realEstateGeneralFields.DISTANCE_PATHWAY]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_PATHWAY]),
            [realEstateGeneralFields.AREA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.AREA]),
            [realEstateGeneralFields.PLOT_LENGTH]: convertNumeralToFloatString(values?.[realEstateGeneralFields.PLOT_LENGTH]),
            [realEstateGeneralFields.PLOT_WIDTH]: convertNumeralToFloatString(values?.[realEstateGeneralFields.PLOT_WIDTH]),
            [realEstateGeneralFields.MAX_CONSTRUCTION_AREA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.MAX_CONSTRUCTION_AREA]),
            [realEstateGeneralFields.CONSTRUCTION_COEFFICIENT]: convertNumeralToFloatString(
                values?.[realEstateGeneralFields.CONSTRUCTION_COEFFICIENT]
            ),
            [realEstateGeneralFields.CONSTRUCTION_FLOORS_NO]: convertNumeralToFloatString(values?.[realEstateGeneralFields.CONSTRUCTION_FLOORS_NO]),
        }),
    }

    return validationObject
}

const propertyGeneralLandLeaseValidaton = (appSettings, data) => {
    const valueDefinition = propertyGeneralValueDefinition(data)
    const validationSchema = {
        ...propertySharedInitialValidation(false),
        [realEstateGeneralFields.PROPERTY_LAND_USAGE_ID]: Yup.number(),
        [realEstateGeneralFields.MORTGAGE_INFO]: Yup.string(),
        [realEstateGeneralFields.AREA]: Yup.string().required(i18n.t('validation.message')),
        [realEstateGeneralFields.CONSTRUCTION_COEFFICIENT]: Yup.number(),
        [realEstateGeneralFields.CONSTRUCTION_FLOORS_NO]: Yup.number(),
        [realEstateGeneralFields.PROPERTY_ORIENTATION_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_PUBLIC_TRANSPORTATION_ID]: Yup.array().of(Yup.number()),
        [realEstateGeneralFields.PROPERTY_ACCESS_ID]: Yup.array().of(Yup.number()),
    }

    const initialValues = {
        ...propertySharedInitialValues(data),
        [realEstateGeneralFields.AVAILABLE_FROM_DATE]: data?.rentedToDate ?? '',
        [realEstateGeneralFields.PROPERTY_LAND_USAGE_ID]: data?.propertyLandUsage?.id ?? '',
        [realEstateGeneralFields.MORTGAGE_INFO]: data?.mortgageInfo ?? '',
        [realEstateGeneralFields.AREA]: data?.area?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.PLOT_WIDTH]: data?.plotWidth ?? '',
        [realEstateGeneralFields.PLOT_LENGTH]: data?.plotLength ?? '',
        [realEstateGeneralFields.CONSTRUCTION_COEFFICIENT]: data?.constructionCoefficient ?? '',
        [realEstateGeneralFields.CONSTRUCTION_FLOORS_NO]: data?.constructionFloorsNo ?? '',
        [realEstateGeneralFields.MAX_CONSTRUCTION_AREA]: data?.maxConstructionArea ?? '',
        ...valueDefinition[realEstateGeneralFields.PROPERTY_ORIENTATION_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_PUBLIC_TRANSPORTATION_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_ACCESS_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_PERMIT_ID],
        [realEstateGeneralFields.DISTANCE_PATHWAY]: data?.distancePathway ?? '',
    }

    const validationObject = {
        initialValues: initialValues,
        validationSchema: Yup.object(validationSchema),
        prepareModelForSubmit: (values) => ({
            ...values,
            ...(data?.id && { [realEstateGeneralFields.ID]: data.id }),
            [realEstateGeneralFields.DISTANCE_SEA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_SEA]),
            [realEstateGeneralFields.DISTANCE_RIVER]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_RIVER]),
            [realEstateGeneralFields.DISTANCE_BEACH]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_BEACH]),
            [realEstateGeneralFields.DISTANCE_CITY_CENTRE]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_CITY_CENTRE]),
            [realEstateGeneralFields.DISTANCE_SCHOOL]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_SCHOOL]),
            [realEstateGeneralFields.DISTANCE_PUBLIC_TRANSPORTATION]: convertNumeralToFloatString(
                values?.[realEstateGeneralFields.DISTANCE_PUBLIC_TRANSPORTATION]
            ),
            [realEstateGeneralFields.DISTANCE_PATHWAY]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_PATHWAY]),
            [realEstateGeneralFields.AREA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.AREA]),
            [realEstateGeneralFields.PLOT_LENGTH]: convertNumeralToFloatString(values?.[realEstateGeneralFields.PLOT_LENGTH]),
            [realEstateGeneralFields.PLOT_WIDTH]: convertNumeralToFloatString(values?.[realEstateGeneralFields.PLOT_WIDTH]),
            [realEstateGeneralFields.MAX_CONSTRUCTION_AREA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.MAX_CONSTRUCTION_AREA]),
            [realEstateGeneralFields.CONSTRUCTION_COEFFICIENT]: convertNumeralToFloatString(
                values?.[realEstateGeneralFields.CONSTRUCTION_COEFFICIENT]
            ),
            [realEstateGeneralFields.CONSTRUCTION_FLOORS_NO]: convertNumeralToFloatString(values?.[realEstateGeneralFields.CONSTRUCTION_FLOORS_NO]),
        }),
    }

    return validationObject
}

const propertyGeneralGarageSaleValidaton = (appSettings, data) => {
    const valueDefinition = propertyGeneralValueDefinition(data)
    const validationSchema = {
        ...propertySharedInitialValidation(true),
        [realEstateGeneralFields.MORTGAGE_INFO]: Yup.string(),
        [realEstateGeneralFields.AREA]: Yup.string().required(i18n.t('validation.message')),
        [realEstateGeneralFields.FLOOR_NO]: Yup.number(),
        [realEstateGeneralFields.FLOORS_TOTAL_NO]: Yup.number(),
        [realEstateGeneralFields.PROPERTY_PERMIT_ID]: Yup.array().of(Yup.number()),
    }

    const initialValues = {
        ...propertySharedInitialValues(data, true),
        [realEstateGeneralFields.COMMON_MAINTENANCE_COSTS]: data?.commonMaintenanceCosts?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.COMMON_MAINTENANCE_COSTS_TYPE]: data?.commonMaintenanceCostsType ?? '€/m2',
        [realEstateGeneralFields.MORTGAGE_INFO]: data?.mortgageInfo ?? '',
        [realEstateGeneralFields.EXCHANGE_POSSIBLE]: data?.exchangePossible ?? '',
        [realEstateGeneralFields.AREA]: data?.area?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.CEILING_HEIGHT]: data?.ceilingHeight ?? '',
        [realEstateGeneralFields.CONSTRUCTION_YEAR]: data?.constructionYear ?? '',
        ...valueDefinition[realEstateGeneralFields.PROPERTY_FLOOR_POSITION_ID],
        ...valueDefinition[realEstateGeneralFields.PROPERTY_PERMIT_ID],
        [realEstateGeneralFields.FLOOR_NO]: data?.floorNo ?? '',
        [realEstateGeneralFields.FLOORS_TOTAL_NO]: data?.floorsTotalNo ?? '',
        [realEstateGeneralFields.PRICE_INITIAL]: data?.priceInitial?.toString().replace(/\./g, ',') ?? '',
    }

    const validationObject = {
        initialValues: initialValues,
        validationSchema: Yup.object(validationSchema),
        prepareModelForSubmit: (values) => ({
            ...values,
            ...(data?.id && { [realEstateGeneralFields.ID]: data.id }),
            [realEstateGeneralFields.MORTGAGE_INFO]: convertNumeralToFloatString(values?.[realEstateGeneralFields.MORTGAGE_INFO]),
            [realEstateGeneralFields.CEILING_HEIGHT]: convertNumeralToFloatString(values?.[realEstateGeneralFields.CEILING_HEIGHT]),
            [realEstateGeneralFields.DISTANCE_SEA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_SEA]),
            [realEstateGeneralFields.DISTANCE_RIVER]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_RIVER]),
            [realEstateGeneralFields.DISTANCE_BEACH]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_BEACH]),
            [realEstateGeneralFields.DISTANCE_CITY_CENTRE]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_CITY_CENTRE]),
            [realEstateGeneralFields.DISTANCE_SCHOOL]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_SCHOOL]),
            [realEstateGeneralFields.DISTANCE_PUBLIC_TRANSPORTATION]: convertNumeralToFloatString(
                values?.[realEstateGeneralFields.DISTANCE_PUBLIC_TRANSPORTATION]
            ),
            [realEstateGeneralFields.AREA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.AREA]),
        }),
    }

    return validationObject
}

const propertyGeneralGarageLeaseValidaton = (appSettings, data) => {
    const valueDefinition = propertyGeneralValueDefinition(data)
    const validationSchema = {
        ...propertySharedInitialValidation(false),
        [realEstateGeneralFields.AREA]: Yup.string().required(i18n.t('validation.message')),
        [realEstateGeneralFields.FLOOR_NO]: Yup.number(),
        [realEstateGeneralFields.FLOORS_TOTAL_NO]: Yup.number(),
        [realEstateGeneralFields.PROPERTY_PERMIT_ID]: Yup.array().of(Yup.number()),
    }

    const initialValues = {
        ...propertySharedInitialValues(data, false),
        [realEstateGeneralFields.COMMON_MAINTENANCE_COSTS]: data?.commonMaintenanceCosts?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.COMMON_MAINTENANCE_COSTS_TYPE]: data?.commonMaintenanceCostsType ?? '€/m2',
        [realEstateGeneralFields.COMMON_UTILITY_COSTS]: data?.commonUtilityCosts?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.COMMON_UTILITY_COSTS_TYPE]: data?.commonUtilityCostsType ?? '€/m2',
        [realEstateGeneralFields.AVAILABLE_FROM_DATE]: data?.rentedToDate ?? '',
        [realEstateGeneralFields.AREA]: data?.area?.toString().replace(/\./g, ',') ?? '',
        [realEstateGeneralFields.CEILING_HEIGHT]: data?.ceilingHeight ?? '',
        [realEstateGeneralFields.CONSTRUCTION_YEAR]: data?.constructionYear ?? '',
        ...valueDefinition[realEstateGeneralFields.PROPERTY_FLOOR_POSITION_ID],
        [realEstateGeneralFields.FLOOR_NO]: data?.floorNo ?? '',
        [realEstateGeneralFields.FLOORS_TOTAL_NO]: data?.floorsTotalNo ?? '',
        ...valueDefinition[realEstateGeneralFields.PROPERTY_PERMIT_ID],
    }

    const validationObject = {
        initialValues: initialValues,
        validationSchema: Yup.object(validationSchema),
        prepareModelForSubmit: (values) => ({
            ...values,
            ...(data?.id && { [realEstateGeneralFields.ID]: data.id }),
            [realEstateGeneralFields.MAINTENANCE_COSTS]: convertNumeralToFloatString(values?.[realEstateGeneralFields.MAINTENANCE_COSTS]),
            [realEstateGeneralFields.COMMON_UTILITY_COSTS]: convertNumeralToFloatString(values?.[realEstateGeneralFields.COMMON_UTILITY_COSTS]),
            [realEstateGeneralFields.CEILING_HEIGHT]: convertNumeralToFloatString(values?.[realEstateGeneralFields.CEILING_HEIGHT]),
            [realEstateGeneralFields.DISTANCE_SEA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_SEA]),
            [realEstateGeneralFields.DISTANCE_RIVER]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_RIVER]),
            [realEstateGeneralFields.DISTANCE_BEACH]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_BEACH]),
            [realEstateGeneralFields.DISTANCE_CITY_CENTRE]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_CITY_CENTRE]),
            [realEstateGeneralFields.DISTANCE_SCHOOL]: convertNumeralToFloatString(values?.[realEstateGeneralFields.DISTANCE_SCHOOL]),
            [realEstateGeneralFields.DISTANCE_PUBLIC_TRANSPORTATION]: convertNumeralToFloatString(
                values?.[realEstateGeneralFields.DISTANCE_PUBLIC_TRANSPORTATION]
            ),
            [realEstateGeneralFields.AREA]: convertNumeralToFloatString(values?.[realEstateGeneralFields.AREA]),
            [realEstateGeneralFields.COMMON_MAINTENANCE_COSTS]: convertNumeralToFloatString(
                values?.[realEstateGeneralFields.COMMON_MAINTENANCE_COSTS]
            ),
        }),
    }

    return validationObject
}

// used in flat sale/lease, office sale/lease validations
// shows validation message if both fields are empty (at least one of the two fields must be filled)
const propertyFloorGroupValidation = () => {
    return {
        [realEstateGeneralFields.FLOOR_NO]: Yup.number().when('property_floor_position_id', {
            is: (val) => !val || val.length === 0,
            then: (schema) => schema.required(i18n.t('validation.message')),
            otherwise: (schema) => schema.notRequired(),
        }),

        [realEstateGeneralFields.PROPERTY_FLOOR_POSITION_ID]: Yup.array().when('floor_no', {
            is: (val) => !val,
            then: (schema) => schema.min(1, i18n.t('validation.message')),
            otherwise: (schema) => schema.notRequired(),
        }),
    }
}

const realEstateDescriptionValidation = (translations, languages = []) => {
    const propertyTitleInitialValues = {}
    const propertyTitleValidationSchema = {}
    const propertyDescriptionInitialValue = {}
    const propertyDescriptionValidationSchema = {}

    for (let lang of languages) {
        propertyTitleInitialValues[lang] = translations?.[lang]?.propertyTitle ?? ''
        propertyTitleValidationSchema[lang] = Yup.string().max(70, i18n.t('validation.title'))
        propertyDescriptionInitialValue[lang] = translations?.[lang]?.propertyDescription ?? ''
        propertyTitleValidationSchema[lang] = Yup.string().max(2000, i18n.t('validation.description'))
    }
    return {
        initialValues: {
            [realEstateDescriptionFields.PROPERTY_TITLE]: propertyTitleInitialValues,
            [realEstateDescriptionFields.PROPERTY_DESCRIPTION]: propertyDescriptionInitialValue,
            [realEstateDescriptionFields.PROPERTY_DESCRIPTION_EMPHASIS]: null,
            [realEstateDescriptionFields.PROPERTY_DESCRIPTION_LANG]: null,
        },
        validationSchema: Yup.object({
            [realEstateDescriptionFields.PROPERTY_TITLE]: Yup.object().shape(propertyTitleValidationSchema),
            [realEstateDescriptionFields.PROPERTY_DESCRIPTION]: Yup.object().shape(propertyDescriptionValidationSchema),
        }),
    }
}

const propertyExportValidation = (data) => {
    const propertyWebsiteInitValues = []
    let propertyPortalCustomValue = []

    if (data?.propertyWebsite && data?.propertyWebsite.length) {
        const propertyWebsite = data.propertyWebsite.map(({ website, ...rest }) => ({
            ...rest,
            website_id: website.id,
        }))

        propertyWebsiteInitValues.push(...propertyWebsite)
    }

    if (data?.propertyPortalCustomValue) {
        const jsonParse = JSON.parse(data?.propertyPortalCustomValue)
        propertyPortalCustomValue = [...jsonParse]
    }

    return {
        initialValues: {
            [propertyExportFields.PROPERTY_PORTAL_ID]:
                data?.propertyPortal && data?.propertyPortal.length ? data?.propertyPortal.map((x) => x.id) : [],
            [propertyExportFields.PROPERTY_WEBSITE_ID]: propertyWebsiteInitValues,
            [propertyExportFields.PROPERTY_PORTAL_CUSTOM_VALUE]: propertyPortalCustomValue,
        },
        validationSchema: Yup.object({}),
    }
}

const propertyTransactionsValidation = (appSettings, data, propertyServiceValue, requiredFields) => {
    const dataEntity = data?.propertyTransaction
    const IS_HR_MARKET = appSettings?.applicationMarket?.name === 'Hrvatska'
    const IS_SI_MARKET = appSettings?.applicationMarket?.name === 'Slovenija'
    const isReservedOrSoldRented = requiredFields.soldRented || requiredFields.reserved
    const isSoldRented = requiredFields.soldRented
    const isReserved = requiredFields.reserved

    return {
        initialValues: {
            [propertyTransactionsFields.PROPERTY_STATUS_ID]: data?.propertyStatus?.id ?? '',
            [propertyTransactionsFields.PROPERTY_TRANSACTION_AGENT_ID]: dataEntity?.propertyTransactionAgent?.id ?? '',
            [propertyTransactionsFields.RESERVATION_DATE]: dataEntity?.reservationDate ?? '',
            contact_collection:
                dataEntity?.contact && dataEntity.contact.length
                    ? dataEntity.contact.map((item) => ({
                          [propertyTransactionsFields.CONTACT_ID]: item.id,
                      }))
                    : [
                          {
                              [propertyTransactionsFields.CONTACT_ID]: '',
                          },
                      ],
            owner_collection: resolveValueByType(data?.owner, realEstateGeneralFields.OWNER_ID),
            [propertyTransactionsFields.PRICE_FINAL]: dataEntity?.priceFinal?.toString().replace(/\./g, ',') ?? '',
            [propertyTransactionsFields.COMMISION_SELLER]: dataEntity?.commissionSeller?.toString().replace(/\./g, ',') ?? '',
            [propertyTransactionsFields.COMMISION_SELLER_TYPE_ID]: dataEntity?.commissionSellerType?.toString() ?? '',
            [propertyTransactionsFields.COMMISION_BUYER]: dataEntity?.commissionBuyer?.toString().replace(/\./g, ',') ?? '',
            [propertyTransactionsFields.COMMISION_BUYER_TYPE_ID]: dataEntity?.commissionBuyerType?.toString() ?? '',
            [propertyTransactionsFields.PROPERTY_MAIN_REGISTER_ID]: dataEntity?.propertyMainRegister?.id?.toString() ?? '',
            [propertyTransactionsFields.PROPERTY_MAIN_REGISTER_DEPARTMENT_ID]: dataEntity?.propertyMainRegisterDepartment?.id?.toString(),
            [propertyTransactionsFields.PROPERTY_REGISTRY_MUNICIPALITY]: dataEntity?.propertyRegistryMunicipality ?? '',
            [propertyTransactionsFields.PROPERTY_REGISTRY_DEPARTMENT_KPU_SUBINSERT_ID]:
                dataEntity?.propertyRegistryDepartmentKpuSubinsert?.id?.toString() ?? '',
            [propertyTransactionsFields.PROPERTY_PUK_ID]: dataEntity?.propertyPuk?.id ?? '',
            ...(propertyServiceValue === REAL_ESTATE_SERVICE.sale
                ? {
                      [propertyTransactionsFields.PROPERTY_LAND_REGISTRY_STATUS_ID]: dataEntity?.propertyLandRegistryStatus?.id?.toString() ?? '',
                      [propertyTransactionsFields.PROPERTY_OWNERSHIP_ACQUISITION_ID]: dataEntity?.propertyOwnershipAcquisition?.id?.toString() ?? '',
                      [propertyTransactionsFields.PROPERTY_OWNERSHIP_ACQUISITION_PROOF_ID]:
                          dataEntity?.propertyOwnershipAcquisitionProof?.id?.toString() ?? '',
                  }
                : {}),
            [propertyTransactionsFields.ADVANCE_AMOUNT]: dataEntity?.advanceAmount?.toString()?.replace(/\./g, ',') ?? '',
            [propertyTransactionsFields.ADVANCE_DATE]: data?.propertyTransaction?.advancePaymentDate ?? '',
            [propertyTransactionsFields.CONCLUSION_DATE]: dataEntity?.conclusionDate ?? '',
            [propertyTransactionsFields.CONCLUSION_PLACE]: dataEntity?.conclusionPlace ?? '',
            [propertyTransactionsFields.FINAL_PAYMENT_DATE]: dataEntity?.finalPaymentDate ?? '',
            [propertyTransactionsFields.PROPERTY_TRANSACTION_RISK_DEGREE_ID]: dataEntity?.propertyTransactionRiskDegree?.id?.toString() ?? '',
            [propertyTransactionsFields.PROPERTY_REGISTRY_INSERT]: dataEntity?.propertyRegistryInsert ?? '',
            [propertyTransactionsFields.PROPERTY_REGISTRY_PARCEL_NO]: dataEntity?.propertyRegistryParcelNo ?? '',
            [propertyTransactionsFields.PROPERTY_REGISTRY_SUBINSERT]: dataEntity?.propertyRegistrySubinsert ?? '',
            [propertyTransactionsFields.PROPERTY_REGISTRY_DEPARTMENT_KPU_ID]: dataEntity?.propertyRegistryDepartmentKpu?.id?.toString() ?? '',
            [propertyTransactionsFields.PROPERTY_SUBINSERT_KPU]: dataEntity?.propertySubinsertKpu ?? '',
            [propertyTransactionsFields.PROPERTY_PUK_DEPARTMENT_ID]: dataEntity?.propertyPukDepartment?.id?.toString() ?? '',
            [propertyTransactionsFields.PROPERTY_CADASTRAL_MUNCIPALITY]: dataEntity?.propertyCadastralMunicipality ?? '',
            [propertyTransactionsFields.PROPERTY_CADASTRAL_PARCEL_NO]: dataEntity?.propertyCadastralParcelNo ?? '',
            [propertyTransactionsFields.PROPERTY_POSSESSORY_SHEET_NO]: dataEntity?.propertyPossessorySheetNo ?? '',
            ...(IS_SI_MARKET && { [propertyTransactionsFields.PROPERTY_BUILDING_NUMBER]: dataEntity?.propertyBuildingNumber ?? '' }),
            ...(IS_SI_MARKET && { [propertyTransactionsFields.PROPERTY_BUILDING_PART_NUMBER]: dataEntity?.propertyBuildingPartNumber ?? '' }),
        },
        validationSchema: Yup.object({
            [propertyTransactionsFields.PROPERTY_STATUS_ID]: Yup.string().required(i18n.t('validation.message')),
            [propertyTransactionsFields.RESERVATION_DATE]: isReserved ? Yup.string().required(i18n.t('validation.message')) : Yup.string(),
            contact_collection: Yup.array().of(
                Yup.object().shape({
                    [propertyTransactionsFields.CONTACT_ID]: isReservedOrSoldRented
                        ? Yup.string().nullable().required(i18n.t('validation.message'))
                        : Yup.string().nullable(),
                })
            ),
            owner_collection: Yup.array().of(
                Yup.object().shape({
                    [realEstateGeneralFields.OWNER_ID]:
                        isReservedOrSoldRented && IS_HR_MARKET
                            ? Yup.string().nullable().required(i18n.t('validation.message'))
                            : Yup.string().nullable(),
                })
            ),
            [propertyTransactionsFields.PRICE_FINAL]: isSoldRented ? Yup.string().required(i18n.t('validation.message')) : Yup.string(),
            [propertyTransactionsFields.CONCLUSION_PLACE]: isSoldRented ? Yup.string().required(i18n.t('validation.message')) : Yup.string(),
            [propertyTransactionsFields.CONCLUSION_DATE]: isSoldRented ? Yup.string().required(i18n.t('validation.message')) : Yup.string(),
            [propertyTransactionsFields.FINAL_PAYMENT_DATE]:
                !IS_SI_MARKET && isSoldRented ? Yup.string().required(i18n.t('validation.message')) : Yup.string(),
            [propertyTransactionsFields.PROPERTY_TRANSACTION_RISK_DEGREE_ID]:
                !IS_SI_MARKET && isSoldRented ? Yup.string().required(i18n.t('validation.message')) : Yup.string(),

            // cadastral data for sold/rented property is required only for HR market
            [propertyTransactionsFields.PROPERTY_MAIN_REGISTER_ID]:
                IS_HR_MARKET && isSoldRented ? Yup.string().required(i18n.t('validation.message')) : Yup.string(),
            [propertyTransactionsFields.PROPERTY_MAIN_REGISTER_DEPARTMENT_ID]:
                IS_HR_MARKET && isSoldRented ? Yup.string().required(i18n.t('validation.message')) : Yup.string(),
            [propertyTransactionsFields.PROPERTY_REGISTRY_MUNICIPALITY]:
                IS_HR_MARKET && isSoldRented ? Yup.string().required(i18n.t('validation.message')) : Yup.string(),
            [propertyTransactionsFields.PROPERTY_REGISTRY_INSERT]:
                IS_HR_MARKET && isSoldRented ? Yup.string().required(i18n.t('validation.message')) : Yup.string(),
            [propertyTransactionsFields.PROPERTY_CADASTRAL_MUNCIPALITY]:
                IS_HR_MARKET && isSoldRented ? Yup.string().required(i18n.t('validation.message')) : Yup.string(),
            [propertyTransactionsFields.PROPERTY_CADASTRAL_PARCEL_NO]:
                IS_HR_MARKET && isSoldRented
                    ? Yup.string().when([propertyTransactionsFields.PROPERTY_REGISTRY_PARCEL_NO], {
                          is: undefined,
                          then: Yup.string().required(i18n.t('validation.message')),
                      })
                    : Yup.string(),
        }),
        prepareModelForSubmit: (values) => {
            const contact_collection =
                values.contact_collection?.map(({ [propertyTransactionsFields.CONTACT_ID]: contactId }) => contactId).filter(Boolean) || []

            return {
                ...values,
                contact_collection,
                [propertyTransactionsFields.PRICE_FINAL]: values?.[propertyTransactionsFields.PRICE_FINAL]
                    ?.toString()
                    .replace(/\./g, '')
                    .replace(/,/g, '.'),
                [propertyTransactionsFields.ADVANCE_AMOUNT]: values?.[propertyTransactionsFields.ADVANCE_AMOUNT]
                    ?.toString()
                    .replace(/\./g, '')
                    .replace(/,/g, '.'),
                [propertyTransactionsFields.COMMISION_SELLER]: values?.[propertyTransactionsFields.COMMISION_SELLER]
                    ?.toString()
                    .replace(/\./g, '')
                    .replace(/,/g, '.'),
                [propertyTransactionsFields.COMMISION_BUYER]: values?.[propertyTransactionsFields.COMMISION_BUYER]
                    ?.toString()
                    .replace(/\./g, '')
                    .replace(/,/g, '.'),
            }
        },
    }
}

const realEstateMaterials3DVideoValidation = (data) => {
    return {
        initialValues: {
            [realEstateMaterials3DVideoFields.VIRTUAL_WALK_URL]: data?.[propertyResponseFields.VIRTUAL_WALK_URL] ?? '',
            [realEstateMaterials3DVideoFields.PROPERTY_VIDEO_URL]: data?.[propertyResponseFields.PROPERTY_VIDEO_URL] ?? '',
        },
        validationSchema: Yup.object({
            [realEstateMaterials3DVideoFields.VIRTUAL_WALK_URL]: Yup.string(),
            [realEstateMaterials3DVideoFields.PROPERTY_VIDEO_URL]: Yup.string(),
        }),
    }
}

const offerValidation = (data) => {
    return {
        initialValues: {
            [offerModalFields.AGENT_ID]: data?.agent?.id?.toString() ?? '',
            [offerModalFields.CONTACT_ID]: data?.offerEmailSettingsGroup?.contact_id ?? '',
            [offerModalFields.CONTACT_NAME]: data?.offerEmailSettingsGroup?.contact_name ?? '',
            [offerModalFields.CONTACT_EMAIL]: data?.offerEmailSettingsGroup?.contact_email ?? '',
            [offerModalFields.CONTACT_COMPANY_COLLECTION]: data?.offerEmailSettingsGroup?.contact_company_collection ?? [],
            [offerModalFields.OFFER_LOCALE]: data?.offerLocale ?? '',
            [offerModalFields.OFFER_TEMPLATE]:
                data?.offerTemplate ?? (data?.offerType === 'property' ? OfferTemplateId.PROPERTY_TEMPLATE_1 : OfferTemplateId.PROJECT_TEMPLATE),
            [offerModalFields.OFFER_TYPE]: data?.offerType ?? '',
            [offerModalFields.OFFER_SETTINGS_GROUP]: data?.offerSettingsGroup ?? [],
            [offerModalFields.OFFER_EMAIL_SETTINGS_GROUP]: [],
        },
        validationSchema: Yup.object({}),
    }
}

const propertyDetailSearchValidation = (data) => {
    return {
        initialValues: {
            [propertyDetailSearchFields.INTERNAL_APP_ID]: data?.[propertyDetailSearchFields.INTERNAL_APP_ID] ?? '',
            [propertyDetailSearchFields.INTERNAL_ID]: data?.[propertyDetailSearchFields.INTERNAL_ID] ?? '',
            [propertyDetailSearchFields.PROPERTY_SERVICE]: data?.[propertyDetailSearchFields.PROPERTY_SERVICE] ?? [],
            [propertyDetailSearchFields.PROPERTY_TYPE]: data?.[propertyDetailSearchFields.PROPERTY_TYPE] ?? [],
            [propertyDetailSearchFields.PROPERTY_STATUS]: data?.[propertyDetailSearchFields.PROPERTY_STATUS] ?? [],
            [propertyDetailSearchFields.PROPERTY_SUB_TYPE]: data?.[propertyDetailSearchFields.PROPERTY_SUB_TYPE] ?? [],
            [propertyDetailSearchFields.PRICE_FROM]: data?.[propertyDetailSearchFields.PRICE_FROM] ?? '',
            [propertyDetailSearchFields.PRICE_TO]: data?.[propertyDetailSearchFields.PRICE_TO] ?? '',
            [propertyDetailSearchFields.AREA_FROM]: data?.[propertyDetailSearchFields.AREA_FROM] ?? '',
            [propertyDetailSearchFields.AREA_TO]: data?.[propertyDetailSearchFields.AREA_TO] ?? '',
            [propertyDetailSearchFields.ROOMS_NO_FROM]: data?.[propertyDetailSearchFields.ROOMS_NO_FROM] ?? '',
            [propertyDetailSearchFields.ROOMS_NO_TO]: data?.[propertyDetailSearchFields.ROOMS_NO_TO] ?? '',
            [propertyDetailSearchFields.BEDROOMS_NO_FROM]: data?.[propertyDetailSearchFields.BEDROOMS_NO_FROM] ?? '',
            [propertyDetailSearchFields.BEDROOMS_NO_TO]: data?.[propertyDetailSearchFields.BEDROOMS_NO_TO] ?? '',
            [propertyDetailSearchFields.FLOOR_NO_FROM]: data?.[propertyDetailSearchFields.FLOOR_NO_FROM] ?? '',
            [propertyDetailSearchFields.FLOOR_NO_TO]: data?.[propertyDetailSearchFields.FLOOR_NO_TO] ?? '',
            [propertyDetailSearchFields.PROPERTY_DESCRIPTIONS]: data?.[propertyDetailSearchFields.PROPERTY_DESCRIPTIONS] ?? [],
            [propertyDetailSearchFields.PROPERTY_SPACE]: data?.[propertyDetailSearchFields.PROPERTY_SPACE] ?? [],

            [propertyDetailSearchFields.COUNTRY]: data?.[propertyDetailSearchFields.COUNTRY] ?? [],
            [propertyDetailSearchFields.CITY]: data?.[propertyDetailSearchFields.CITY] ?? [],
            [propertyDetailSearchFields.COUNTY]: data?.[propertyDetailSearchFields.COUNTY] ?? [],
            [propertyDetailSearchFields.WIDER_AREA]: data?.[propertyDetailSearchFields.WIDER_AREA] ?? [],
            [propertyDetailSearchFields.QUARTER]: data?.[propertyDetailSearchFields.QUARTER] ?? [],
            [propertyDetailSearchFields.STREET]: data?.[propertyDetailSearchFields.STREET] ?? '',
            [propertyDetailSearchFields.ISLAND]: data?.[propertyDetailSearchFields.ISLAND] ?? [],
            [propertyDetailSearchFields.DISTANCE_SEA]: data?.[propertyDetailSearchFields.DISTANCE_SEA] ?? '',
            [propertyDetailSearchFields.PROPERTY_UTILITY]: data?.[propertyDetailSearchFields.PROPERTY_UTILITY] ?? [],
            [propertyDetailSearchFields.PROPERTY_EQUIPMENT]: data?.[propertyDetailSearchFields.PROPERTY_EQUIPMENT] ?? [],

            [propertyDetailSearchFields.OWNER]: data?.[propertyDetailSearchFields.OWNER] ?? '',
            [propertyDetailSearchFields.PRIMARY_AGENT]: data?.[propertyDetailSearchFields.PRIMARY_AGENT] ?? [],
            [propertyDetailSearchFields.PHONE_NUMBER]: data?.[propertyDetailSearchFields.PHONE_NUMBER] ?? '',
            [propertyDetailSearchFields.PROPERTY_RECEIPT_DATE_FROM]: data?.[propertyDetailSearchFields.PROPERTY_RECEIPT_DATE_FROM] ?? '',
            [propertyDetailSearchFields.PROPERTY_RECEIPT_DATE_TO]: data?.[propertyDetailSearchFields.PROPERTY_RECEIPT_DATE_TO] ?? '',
            [propertyDetailSearchFields.AVAILABLE_FROM_DATE_FROM]: data?.[propertyDetailSearchFields.AVAILABLE_FROM_DATE_FROM] ?? '',
            [propertyDetailSearchFields.AVAILABLE_FROM_DATE_TO]: data?.[propertyDetailSearchFields.AVAILABLE_FROM_DATE_TO] ?? '',
            [propertyDetailSearchFields.WEB]: data?.[propertyDetailSearchFields.WEB]?.id ?? '',
            [propertyDetailSearchFields.ATTRIBUTE]: data?.[propertyDetailSearchFields.WEB]?.attribute ?? [],
            [propertyDetailSearchFields.PORTAL]: data?.[propertyDetailSearchFields.PORTAL] ?? [],
            [propertyDetailSearchFields.PROPERTY_CONDITION]: data?.[propertyDetailSearchFields.PROPERTY_CONDITION] ?? [],
            [propertyDetailSearchFields.PROPERTY_ENERGY_EFFICIENCY]: data?.[propertyDetailSearchFields.PROPERTY_ENERGY_EFFICIENCY] ?? [],
            [propertyDetailSearchFields.PROPERTY_FLOOR]: data?.[propertyDetailSearchFields.PROPERTY_FLOOR] ?? [],
        },
    }
}

export {
    generalRealEstateFlatLeaseValidaton,
    generalRealEstateFlatSaleValidaton,
    generalRealEstateValidaton,
    offerValidation,
    propertyDetailSearchValidation,
    propertyExportValidation,
    propertyGeneralGarageLeaseValidaton,
    propertyGeneralGarageSaleValidaton,
    propertyGeneralHouseLeaseValidaton,
    propertyGeneralHouseSaleValidaton,
    propertyGeneralLandLeaseValidaton,
    propertyGeneralLandSaleValidaton,
    propertyGeneralOfficeSpaceLeaseValidaton,
    propertyGeneralOfficeSpaceSaleValidaton,
    propertyTransactionsValidation,
    realEstateDescriptionValidation,
    realEstateMaterials3DVideoValidation,
}
